import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { companyTransactions } from '../../api/company/client'
import { format } from 'date-fns'
import { ArrowLeft, Loader2 } from 'lucide-react'

const STATUS_KEYS: Record<string, string> = {
  completed: 'company.statusCompleted',
  pending: 'company.statusPending',
  failed: 'company.statusFailed',
  refunded: 'company.statusRefunded',
}

export default function CompanyTransactionDetail() {
  const { t } = useTranslation('portal')
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['company', 'transaction', id],
    queryFn: async () => (await companyTransactions.get(id!)).data,
    enabled: !!id,
  })

  if (isLoading || !id) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" /> {t('company.transactions.detailLoading')}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div>
        <p className="text-red-600">{t('company.transactions.detailNotFound')}</p>
        <Link to="/company/transactions" className="text-blue-600 hover:underline mt-2 inline-block">
          {t('company.transactions.detailBackToList')}
        </Link>
      </div>
    )
  }

  return (
    <div>
      <Link
        to="/company/transactions"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> {t('company.transactions.detailBack')}
      </Link>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.transactions.detailTitle')}</h1>
      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-6 space-y-4">
          {data.is_internal_receipt && (
            <div>
              <span className="rounded px-2 py-1 text-xs bg-violet-100 text-violet-700">
                {t('company.transactions.internalReceipt')}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-sm">{data.is_internal_receipt ? t('company.transactions.detailReceiptNumber') : t('company.overview.fiscalNumber')}</p>
              <p className="text-gray-900 font-mono">{data.fiscal_number}</p>
            </div>
            {!data.is_internal_receipt && (
              <div>
                <p className="text-gray-500 text-sm">IIC</p>
                <p className="text-gray-900 font-mono text-sm">{data.iic ?? '—'}</p>
              </div>
            )}
            <div>
              <p className="text-gray-500 text-sm">Amount</p>
              <p className="text-gray-900">
                {Number(data.amount).toFixed(2)} {data.currency}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Bank fee</p>
              <p className="text-gray-700">{Number(data.bank_fee).toFixed(2)} {data.currency}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Platform fee</p>
              <p className="text-gray-700">{Number(data.platform_fee).toFixed(2)} {data.currency}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Net amount</p>
              <p className="text-blue-600 font-semibold">
                {Number(data.net_amount).toFixed(2)} {data.currency}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Status</p>
              <span
                className={`inline-block rounded px-2 py-0.5 text-xs ${
                  data.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : data.status === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                }`}
              >
                {STATUS_KEYS[data.status] ? t(STATUS_KEYS[data.status]) : data.status}
              </span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Date</p>
              <p className="text-gray-700">{format(new Date(data.created_at), 'PPpp')}</p>
            </div>
          </div>
          {(data.customer_email || data.customer_phone) && (
            <div className="pt-4 border-t border-gray-200">
              <p className="text-gray-500 text-sm mb-2">Customer</p>
              <p className="text-gray-700">
                {data.customer_email ?? '—'} {data.customer_phone ? `· ${data.customer_phone}` : ''}
              </p>
            </div>
          )}
          {(data.payment_method || data.monri_transaction_id) && (
            <div className="pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.payment_method && (
                <div>
                  <p className="text-gray-500 text-sm">Payment method</p>
                  <p className="text-gray-700">{data.payment_method}</p>
                </div>
              )}
              {data.monri_transaction_id && (
                <div>
                  <p className="text-gray-500 text-sm">Monri transaction ID</p>
                  <p className="text-gray-700 font-mono text-sm">{data.monri_transaction_id}</p>
                </div>
              )}
              {data.monri_order_number && (
                <div>
                  <p className="text-gray-500 text-sm">Order number</p>
                  <p className="text-gray-700 font-mono text-sm">{data.monri_order_number}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
